/**
 * net/badge.js — the little bottom-right readout that says who else is here.
 *
 * Deliberately builds its own DOM and injects its own stylesheet rather than
 * adding markup to `index.html`: eight agents share this checkout and a shared
 * HTML file is a merge conflict waiting to happen. The cost is a few lines of
 * `createElement`; the benefit is that this module owns its entire surface.
 *
 * The badge is also the whole of the offline story. When the broker cannot be
 * reached there is no error, no dialog and no retry spinner — just a line of
 * text saying the carnival is offline and how many geese are wandering about
 * anyway. A player who never reads it loses nothing.
 *
 * @module net/badge
 */

const STYLE_ID = 'cc-net-badge-style';

const CSS = `
#cc-net-badge {
  position: fixed; bottom: 34px; right: 12px; z-index: 46;
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  justify-content: flex-end; max-width: min(92vw, 420px);
  font: 500 12px/1.35 "Trebuchet MS", system-ui, sans-serif;
  color: #cfe0ff; background: rgba(14, 18, 32, 0.72);
  border: 1px solid rgba(143, 228, 255, 0.28); border-radius: 10px;
  padding: 7px 10px; backdrop-filter: blur(4px);
  pointer-events: auto; user-select: none;
}
#cc-net-badge.offline { border-color: rgba(255, 209, 102, 0.3); color: #e6d7b0; }
#cc-net-badge .cc-net-text { white-space: nowrap; }
#cc-net-badge b { color: #ffd166; font-weight: 700; }
#cc-net-badge code {
  color: #8fe4ff; font: 600 12px/1 ui-monospace, "Consolas", monospace;
  letter-spacing: 0.5px;
}
#cc-net-badge button {
  font: 600 11px/1 "Trebuchet MS", sans-serif; color: #0d1220;
  background: #8fe4ff; border: 0; border-radius: 6px; padding: 4px 8px;
  cursor: pointer;
}
#cc-net-badge button:hover { background: #b6efff; }
#cc-net-badge button.ghost {
  background: transparent; color: #8fe4ff;
  border: 1px solid rgba(143, 228, 255, 0.45);
}
#cc-net-badge input {
  width: 92px; font: 600 11px/1 ui-monospace, monospace; color: #cfe0ff;
  background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(143, 228, 255, 0.3);
  border-radius: 6px; padding: 4px 6px; text-transform: uppercase;
}
#cc-net-badge input::placeholder { color: #6d7c99; text-transform: none; }
`;

/**
 * The presence badge. All render paths go through `set()`, so the badge can
 * never disagree with the connection state that produced it.
 */
class Badge {
  constructor() {
    /** @type {HTMLElement|null} */ this.el = null;
    /** @type {HTMLElement|null} */ this.text = null;
    /** @type {HTMLInputElement|null} */ this.input = null;
    this.state = {
      status: 'offline', peers: 0, npcs: 0, room: null, isHost: false,
    };
    /** @type {(code:string)=>void} */
    this.onJoin = () => {};
  }

  /** Create the DOM. Idempotent, and a no-op outside a browser. */
  install() {
    if (this.el || typeof document === 'undefined') return this;

    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = CSS;
      document.head.appendChild(style);
    }

    this.el = document.createElement('div');
    this.el.id = 'cc-net-badge';

    this.text = document.createElement('span');
    this.text.className = 'cc-net-text';
    this.el.appendChild(this.text);

    this.copyBtn = this._button('copy', 'ghost', () => this._copy());
    this.el.appendChild(this.copyBtn);

    this.input = document.createElement('input');
    this.input.placeholder = 'room code';
    this.input.maxLength = 12;
    this.input.setAttribute('aria-label', 'Join a carnival by room code');
    this.input.addEventListener('keydown', (e) => {
      // The game reads raw keys for WASD; a badge that swallows them is a bug.
      e.stopPropagation();
      if (e.key === 'Enter') this._join();
    });
    this.el.appendChild(this.input);

    this.el.appendChild(this._button('join', '', () => this._join()));

    document.body.appendChild(this.el);
    this.render();
    return this;
  }

  /** @private */
  _button(label, className, onClick) {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = label;
    if (className) b.className = className;
    b.addEventListener('click', onClick);
    return b;
  }

  /**
   * Merge new state and re-render.
   * @param {{ status?:string, peers?:number, npcs?:number,
   *           room?:string|null, isHost?:boolean }} patch
   */
  set(patch) {
    Object.assign(this.state, patch);
    this.render();
  }

  render() {
    if (!this.el || !this.text) return;
    const s = this.state;
    const online = s.status === 'connected';

    this.el.classList.toggle('offline', !online);
    this.copyBtn.style.display = online && s.room ? '' : 'none';

    if (s.status === 'connecting') {
      this.text.innerHTML = '🪿 finding the carnival…';
      return;
    }

    if (!online) {
      // Offline is a state, not a failure — so it is phrased as one.
      this.text.innerHTML =
        `🪿 offline carnival · <b>${s.npcs}</b> ${plural(s.npcs, 'goose', 'geese')} nearby`;
      return;
    }

    const total = s.peers + 1;
    this.text.innerHTML =
      `🪿 <b>${total}</b> ${plural(total, 'goose', 'geese')} here · room <code>${escapeHtml(s.room || '—')}</code>`;
  }

  /** @private */
  _copy() {
    const room = this.state.room;
    if (!room) return;
    const url = `${location.origin}${location.pathname}?room=${encodeURIComponent(room)}`;
    navigator.clipboard?.writeText(url).catch(() => {});
    this.copyBtn.textContent = 'copied';
    setTimeout(() => { if (this.copyBtn) this.copyBtn.textContent = 'copy'; }, 1400);
  }

  /** @private */
  _join() {
    const code = (this.input?.value || '').trim().toUpperCase();
    if (!code) return;
    this.onJoin(code);
  }

  /** @returns {object} What the badge is currently showing — for tests. */
  snapshot() {
    return {
      present: Boolean(this.el),
      status: this.state.status,
      peers: this.state.peers,
      npcs: this.state.npcs,
      room: this.state.room,
      text: this.text ? this.text.textContent : '',
      offline: this.el ? this.el.classList.contains('offline') : true,
    };
  }

  dispose() {
    this.el?.remove();
    this.el = null;
    this.text = null;
    this.input = null;
  }
}

/** @param {number} n @param {string} one @param {string} many */
function plural(n, one, many) {
  return n === 1 ? one : many;
}

/** @param {string} s */
function escapeHtml(s) {
  return String(s).replace(/[<>&"']/g, (c) => ({
    '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

/** The one badge. */
export const badge = new Badge();
